'use client';

import { FC } from 'react';

import ContentLayout from '@/layouts/ContentLayout';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import { TypeAuthorsWithoutUnresolvableLinksResponse } from '../../_generated';
import { formatDate } from '@/utils/formatDate';
import { useSettingsContext } from '@/contexts/SettingsContext';
import CustomImage from '@/components/CustomImage/CustomImage';

interface IInsightArticleHeaderProps {
    id: string;
    title: string;
    publishedDate?: string;
    author?: TypeAuthorsWithoutUnresolvableLinksResponse;
}

const InsightArticleHeader: FC<IInsightArticleHeaderProps> = ({
    id,
    title,
    author,
    publishedDate,
}) => {
    const { lang } = useSettingsContext();
    const articleProps = useContentfulInspectorMode({ entryId: id });
    const authorProps = useContentfulInspectorMode({ entryId: author?.sys.id });
    const pubDate = formatDate(lang, publishedDate);
    return (
        <div className="tw-bg-[#f9f9f9] tw-pt-10 tw-pb-11">
            <ContentLayout>
                <div className="tw-w-full sm:tw-w-3/4 tw-mx-auto">
                    <h1
                        className="tw-text-[26px] lg:tw-text-[36px] tw-text-[#003865] tw-font-bold tw-leading-[38px] lg:tw-leading-[52px] tw-pb-4 tw-mb-2"
                        {...articleProps({
                            fieldId: 'title',
                        })}
                    >
                        {title}
                    </h1>
                    <div className="tw-flex tw-gap-3">
                        <div className="tw-w-[58px] tw-h-[58px] tw-overflow-hidden">
                            <CustomImage
                                src={
                                    author?.fields.image?.fields.file?.url
                                        ? `https:${author?.fields.image?.fields.file?.url}`
                                        : '/images/user-fallback.svg'
                                }
                                alt={author?.fields.image?.fields.title ?? 'user'}
                                width={58}
                                height={58}
                                className="tw-h-full tw-w-full tw-object-cover tw-rounded-full tw-border-[3px] tw-border-white tw-border-solid"
                                {...authorProps({
                                    fieldId: 'image',
                                })}
                            />
                        </div>
                        <div className="tw-flex-1">
                            <div
                                className="tw-pt-1 tw-pb-2 tw-font-gotham-bold tw-text-base"
                                {...authorProps({
                                    fieldId: 'name',
                                })}
                            >
                                {author?.fields.name}
                            </div>
                            <div
                                className="tw-text-[#71757acc] tw-text-sm"
                                {...articleProps({
                                    fieldId: 'publishedDate',
                                })}
                            >
                                {pubDate}
                            </div>
                        </div>
                    </div>
                </div>
            </ContentLayout>
        </div>
    );
};

export default InsightArticleHeader;
